/**
 * Interface text in Arabic and English.
 *
 * This is only the app's own chrome (buttons, titles, messages). Question text
 * and answers NEVER come from here — they always come from the question bank so
 * the original English is preserved exactly as supplied.
 */
export interface Bilingual {
  ar: string;
  en: string;
}

export const UI = {
  appName: { ar: 'رخصة أوهايو بالعربي', en: 'Ohio Permit Arabic' },
  appTagline: { ar: 'تدريب على اختبار تصريح القيادة', en: 'Practice for the permit knowledge test' },
  welcome: { ar: 'أهلاً بك', en: 'Welcome' },

  // Onboarding
  chooseLanguage: { ar: 'اختر اللغة', en: 'Choose Language' },
  chooseStyle: { ar: 'اختر طريقة التعلّم', en: 'Choose Learning Style' },
  langArabic: { ar: 'العربية', en: 'Arabic' },
  langArabicEnglish: { ar: 'العربية مع الإنجليزية', en: 'Arabic + English' },
  langEnglish: { ar: 'الإنجليزية', en: 'English' },
  easyMode: { ar: 'وضع التعلّم السهل', en: 'Easy Learning Mode' },
  easyModeHint: { ar: 'سؤال واحد، خط كبير، أزرار كبيرة', en: 'One question, large text, large buttons' },
  standardMode: { ar: 'الوضع العادي', en: 'Standard Mode' },
  standardModeHint: { ar: 'كل الميزات وخيارات أكثر', en: 'All features and more options' },
  recommended: { ar: 'مُوصى به', en: 'Recommended' },
  start: { ar: 'ابدأ', en: 'Start' },
  changeLater: { ar: 'يمكنك تغيير هذا لاحقاً من الإعدادات', en: 'You can change this later in Settings' },

  // Home
  todayPractice: { ar: 'تدريب اليوم', en: "Today's Practice" },
  learn: { ar: 'تعلّم', en: 'Learn' },
  practiceTest: { ar: 'اختبار تجريبي', en: 'Practice Test' },
  roadSigns: { ar: 'إشارات الطريق', en: 'Road Signs' },
  myMistakes: { ar: 'تدريب على أخطائي', en: 'Practice My Mistakes' },
  listenAndAnswer: { ar: 'استمع وأجب', en: 'Listen & Answer' },
  byTopic: { ar: 'التدريب حسب الموضوع', en: 'Practice by Topic' },
  myProgress: { ar: 'تقدّمي', en: 'My Progress' },
  settings: { ar: 'الإعدادات', en: 'Settings' },
  more: { ar: 'المزيد', en: 'More' },

  // Generic controls
  back: { ar: 'رجوع', en: 'Back' },
  next: { ar: 'التالي', en: 'Next' },
  continueLabel: { ar: 'متابعة', en: 'Continue' },
  finish: { ar: 'إنهاء', en: 'Finish' },
  exit: { ar: 'خروج', en: 'Exit' },
  close: { ar: 'إغلاق', en: 'Close' },
  cancel: { ar: 'إلغاء', en: 'Cancel' },
  confirm: { ar: 'تأكيد', en: 'Confirm' },
  save: { ar: 'حفظ', en: 'Save' },
  yes: { ar: 'نعم', en: 'Yes' },
  no: { ar: 'لا', en: 'No' },
  submitAnswer: { ar: 'تأكيد الإجابة', en: 'Submit Answer' },
  tryAgainBtn: { ar: 'حاول مرة أخرى', en: 'Try Again' },
  startAgain: { ar: 'ابدأ من جديد', en: 'Start Again' },
  goHome: { ar: 'الصفحة الرئيسية', en: 'Home' },

  // Audio
  readEverything: { ar: 'استمع إلى كل شيء', en: 'Read Everything' },
  listenAgain: { ar: 'استمع مرة أخرى', en: 'Listen Again' },
  listenQuestion: { ar: 'استمع إلى السؤال', en: 'Listen to the question' },
  listenAnswer: { ar: 'استمع إلى هذه الإجابة', en: 'Listen to this answer' },
  listenExplanation: { ar: 'استمع إلى الشرح', en: 'Listen to the explanation' },
  replay: { ar: 'إعادة', en: 'Replay' },
  pause: { ar: 'إيقاف مؤقت', en: 'Pause' },
  resume: { ar: 'متابعة', en: 'Resume' },
  stop: { ar: 'إيقاف', en: 'Stop' },
  slower: { ar: 'أبطأ', en: 'Slower' },
  normalSpeed: { ar: 'سرعة عادية', en: 'Normal speed' },
  speechSpeed: { ar: 'سرعة الصوت', en: 'Speech speed' },
  speedSlow: { ar: 'بطيء', en: 'Slow' },
  speedDefault: { ar: 'أبطأ قليلاً', en: 'Slightly slower' },
  speedNormal: { ar: 'عادي', en: 'Normal' },
  noArabicVoiceTitle: { ar: 'لا يوجد صوت عربي على هذا الجهاز', en: 'No Arabic voice on this device' },
  noArabicVoiceBody: {
    ar: 'لم يعثر التطبيق على صوت عربي. لن يتم استخدام صوت إنجليزي بدلاً منه. افتح الإعدادات لمعرفة كيفية تثبيت صوت عربي.',
    en: 'No Arabic voice was found. An English voice will NOT be used instead. Open Settings to see how to install an Arabic voice.',
  },
  noEnglishVoice: { ar: 'لا يوجد صوت إنجليزي على هذا الجهاز.', en: 'No English voice is installed on this device.' },
  audioUnavailable: { ar: 'الصوت غير متاح', en: 'Audio unavailable' },

  // Quiz
  questionOf: { ar: 'السؤال', en: 'Question' },
  ofWord: { ar: 'من', en: 'of' },
  chooseAnswer: { ar: 'اختر الإجابة الصحيحة', en: 'Choose the correct answer' },
  tapSpeakerHint: { ar: 'اضغط 🔊 للاستماع', en: 'Press 🔊 to listen' },
  correct: { ar: 'إجابة صحيحة', en: 'Correct' },
  incorrect: { ar: 'إجابة غير صحيحة', en: 'Not correct' },
  tryAgainMsg: { ar: 'حاول مرة أخرى', en: 'Try again' },
  correctAnswerIs: { ar: 'الإجابة الصحيحة هي:', en: 'The correct answer is:' },
  why: { ar: 'لماذا؟', en: 'Why?' },
  explanation: { ar: 'الشرح', en: 'Explanation' },
  answered: { ar: 'تمت الإجابة', en: 'Answered' },
  remaining: { ar: 'المتبقي', en: 'Remaining' },
  correctCount: { ar: 'صحيحة', en: 'Correct' },
  wrongCount: { ar: 'غير صحيحة', en: 'Incorrect' },
  enlargeImage: { ar: 'اضغط على الصورة لتكبيرها', en: 'Tap the picture to enlarge it' },
  noQuestionsYet: { ar: 'لا توجد أسئلة هنا بعد.', en: 'There are no questions here yet.' },
  noMistakesYet: {
    ar: 'رائع! لا توجد أخطاء لتتدرب عليها الآن.',
    en: 'Great! You have no mistakes to practice right now.',
  },

  // Praise
  praiseWellDone: { ar: 'أحسنت!', en: 'Well done!' },
  praiseExcellent: { ar: 'ممتاز!', en: 'Excellent!' },
  praiseKeepGoing: { ar: 'استمر، أنت تتقدّم.', en: 'Keep going, you are making progress.' },
  sessionDone: { ar: 'انتهت الجلسة', en: 'Session complete' },
  greatJob: { ar: 'عمل رائع!', en: 'Great job!' },
  yourScore: { ar: 'نتيجتك', en: 'Your score' },

  // Practice test
  testIntroTitle: { ar: 'الاختبار التجريبي', en: 'Practice Test' },
  testIntroBody: {
    ar: '40 سؤالاً. تحتاج إلى 30 إجابة صحيحة للنجاح. لا يوجد وقت محدد.',
    en: '40 questions. You need 30 correct answers to pass. There is no time limit.',
  },
  startTest: { ar: 'ابدأ الاختبار', en: 'Start the test' },
  passed: { ar: 'ناجح', en: 'PASS' },
  keepPracticing: { ar: 'واصل التدريب', en: 'KEEP PRACTICING' },
  passingScore: { ar: 'درجة النجاح: 30 من 40', en: 'Passing score: 30 of 40' },
  reviewAnswers: { ar: 'مراجعة الإجابات', en: 'Review answers' },
  yourAnswer: { ar: 'إجابتك', en: 'Your answer' },
  notAnswered: { ar: 'بدون إجابة', en: 'Not answered' },

  // Road signs
  learnSigns: { ar: 'تعلّم الإشارات', en: 'Learn Signs' },
  testSigns: { ar: 'اختبرني في الإشارات', en: 'Test Me on Signs' },
  signMeaning: { ar: 'معنى الإشارة', en: 'What it means' },
  whatDoesSignMean: { ar: 'ماذا تعني هذه الإشارة؟', en: 'What does this sign mean?' },
  showMeaning: { ar: 'أظهر المعنى', en: 'Show the meaning' },
  hideMeaning: { ar: 'إخفاء المعنى', en: 'Hide the meaning' },
  signOf: { ar: 'إشارة', en: 'Sign' },
  markLearned: { ar: 'تعلّمتها ✅', en: 'I learned it ✅' },
  markPractice: { ar: 'تحتاج تدريب ⭐', en: 'Needs practice ⭐' },

  // Categories / progress
  topics: { ar: 'المواضيع', en: 'Topics' },
  learnedLabel: { ar: 'تم تعلّمه', en: 'Learned' },
  needsPractice: { ar: 'يحتاج تدريب', en: 'Needs practice' },
  totalQuestions: { ar: 'مجموع الأسئلة', en: 'Total questions' },
  questionsSeen: { ar: 'أسئلة رأيتها', en: 'Questions seen' },
  questionsLearned: { ar: 'أسئلة تعلّمتها', en: 'Questions learned' },
  questionsNeedPractice: { ar: 'أسئلة تحتاج تدريباً', en: 'Questions needing practice' },
  testsTaken: { ar: 'اختبارات أجريتها', en: 'Practice tests taken' },
  testsPassed: { ar: 'اختبارات نجحت فيها', en: 'Practice tests passed' },
  signsLearned: { ar: 'إشارات تعلّمتها', en: 'Road signs learned' },
  studyTime: { ar: 'وقت الدراسة', en: 'Study time' },
  accuracy: { ar: 'نسبة الإجابات الصحيحة', en: 'Accuracy' },

  // Settings
  language: { ar: 'اللغة', en: 'Language' },
  displayMode: { ar: 'طريقة العرض', en: 'Display' },
  fontSize: { ar: 'حجم الخط', en: 'Text size' },
  fontNormal: { ar: 'عادي', en: 'Normal' },
  fontLarge: { ar: 'كبير', en: 'Large' },
  fontXLarge: { ar: 'كبير جداً', en: 'Extra large' },
  highContrast: { ar: 'تباين عالٍ', en: 'High contrast' },
  reducedMotion: { ar: 'تقليل الحركة', en: 'Reduce motion' },
  learningStyle: { ar: 'طريقة التعلّم', en: 'Learning style' },
  autoSpeak: { ar: 'اقرأ السؤال تلقائياً', en: 'Read the question automatically' },
  speakFeedback: { ar: 'انطق النتيجة بعد كل إجابة', en: 'Speak the result after each answer' },
  voice: { ar: 'الصوت العربي', en: 'Arabic voice' },
  englishVoice: { ar: 'الصوت الإنجليزي', en: 'English voice' },
  testVoice: { ar: 'جرّب الصوت', en: 'Test the voice' },
  audioEngine: { ar: 'محرك الصوت', en: 'Speech engine' },
  engineBrowser: { ar: 'صوت المتصفح', en: 'Browser voice' },
  enginePiper: { ar: 'خادم صوت محلي (Piper)', en: 'Local voice server (Piper)' },
  piperUrl: { ar: 'عنوان الخادم المحلي', en: 'Local server address' },
  dataAndPrivacy: { ar: 'البيانات والخصوصية', en: 'Data and privacy' },
  exportProgress: { ar: 'حفظ نسخة من التقدّم', en: 'Export progress' },
  importProgress: { ar: 'استعادة التقدّم من ملف', en: 'Import progress' },
  resetProgress: { ar: 'حذف كل التقدّم', en: 'Reset all progress' },
  resetConfirm: {
    ar: 'هل تريد بالتأكيد حذف كل التقدّم؟ لا يمكن التراجع عن هذا.',
    en: 'Are you sure you want to delete all progress? This cannot be undone.',
  },
  resetDone: { ar: 'تم حذف التقدّم.', en: 'Progress has been reset.' },
  importDone: { ar: 'تم استيراد التقدّم.', en: 'Progress imported.' },
  importFailed: { ar: 'تعذّر قراءة الملف.', en: 'That file could not be read.' },
  privacyNote: {
    ar: 'كل شيء يبقى على هذا الجهاز. لا يوجد حساب ولا إنترنت ولا تتبّع.',
    en: 'Everything stays on this device. No account, no internet, no tracking.',
  },

  // Caregiver
  caregiver: { ar: 'تفاصيل التقدّم', en: 'Progress Details' },
  caregiverNote: {
    ar: 'هذه الصفحة للأهل أو مقدّم الرعاية.',
    en: 'This page is for a parent or caregiver.',
  },
  mostMissed: { ar: 'الأسئلة الأكثر خطأً', en: 'Most-missed questions' },
  byCategory: { ar: 'حسب الموضوع', en: 'By topic' },
  recentSessions: { ar: 'الجلسات الأخيرة', en: 'Recent sessions' },
  mastered: { ar: 'أسئلة أتقنها', en: 'Mastered questions' },
  needsReview: { ar: 'أسئلة تحتاج مراجعة', en: 'Questions needing review' },
  bankIssues: { ar: 'مشاكل في ملفات الأسئلة', en: 'Question file problems' },
  noData: { ar: 'لا توجد بيانات بعد.', en: 'No data yet.' },

  // Question manager
  questionManager: { ar: 'إدارة الأسئلة', en: 'Question Manager' },
  managerIntro: {
    ar: 'أضف أسئلة جديدة تُحفظ على هذا الجهاز، أو نزّل ملف JSON لإضافته إلى ملفات المشروع.',
    en: 'Add questions that are saved on this device, or download a JSON file to add to the project files.',
  },
  addQuestion: { ar: 'إضافة سؤال', en: 'Add question' },
  customQuestions: { ar: 'الأسئلة المضافة محلياً', en: 'Locally added questions' },
  downloadJson: { ar: 'تنزيل ملف JSON', en: 'Download JSON' },
  copyJson: { ar: 'نسخ JSON', en: 'Copy JSON' },
  copied: { ar: 'تم النسخ', en: 'Copied' },
  deleteLabel: { ar: 'حذف', en: 'Delete' },
  requiredField: { ar: 'حقل مطلوب', en: 'Required' },
  correctAnswerLabel: { ar: 'الإجابة الصحيحة', en: 'Correct answer' },
  imagePath: { ar: 'مسار الصورة', en: 'Image path' },
  optional: { ar: 'اختياري', en: 'Optional' },
  preview: { ar: 'معاينة', en: 'Preview' },

  // Demo notice
  demoBadge: { ar: 'محتوى تجريبي', en: 'Demo content' },
  demoNotice: {
    ar: 'هذه أسئلة تجريبية كُتبت لهذا التطبيق من مصادر رسمية متاحة للعموم، وليست أسئلة الاختبار الرسمي.',
    en: 'These are demonstration questions written for this app from publicly available official material. They are not the official BMV test questions.',
  },

  // Keyboard help
  keyboardHelp: { ar: 'اختصارات لوحة المفاتيح', en: 'Keyboard shortcuts' },
  keyAnswer: { ar: '1 إلى 4 : اختيار إجابة', en: '1–4 : choose an answer' },
  notFinished: { ar: 'لم يكتمل', en: 'Not finished' },
  keyRead: { ar: 'مسافة أو R : استمع إلى كل شيء', en: 'Space or R : read everything' },
  keyPause: { ar: 'P : إيقاف مؤقت / متابعة', en: 'P : pause / resume' },
  keyStop: { ar: 'S : إيقاف الصوت', en: 'S : stop audio' },
  keyNext: { ar: 'Enter : التالي', en: 'Enter : next' },
  skipToContent: { ar: 'انتقل إلى المحتوى', en: 'Skip to content' },

  /* Arabic voice ---------------------------------------------------- */
  voiceInUse: { ar: 'الصوت المستخدم الآن', en: 'Voice in use right now' },
  voiceBundled: { ar: 'الصوت العربي المرفق مع التطبيق', en: "The app's own Arabic voice" },
  voiceDevice: { ar: 'الصوت العربي في هذا الجهاز', en: "This device's Arabic voice" },
  voiceAutomatic: { ar: 'تلقائي', en: 'Automatic' },
  voiceBundledNote: {
    ar: 'يعمل هذا الصوت دائماً، بدون إنترنت وبدون تثبيت أي شيء.',
    en: 'This voice always works — no internet, nothing to install.',
  },
  voiceDeviceNote: {
    ar: 'هذا الجهاز فيه صوت عربي، والتطبيق يستخدمه لأنه عادةً أوضح. يمكنك اختيار الصوت المرفق من القائمة.',
    en: "This device has its own Arabic voice and the app is using it, since it is usually clearer. You can pick the app's voice from the list instead.",
  },
  installDeviceVoice: { ar: 'كيف أضيف صوتاً عربياً لهذا الجهاز؟', en: 'How to add an Arabic voice to this device' },
  offlineAudio: { ar: 'الصوت بدون إنترنت', en: 'Audio without internet' },
  downloadAudio: { ar: 'تنزيل كل الأصوات', en: 'Download all the audio' },
  downloadingAudio: { ar: 'جارٍ التنزيل…', en: 'Downloading…' },
  audioReadyOffline: { ar: 'كل الأصوات جاهزة للاستخدام بدون إنترنت.', en: 'All the audio is ready to use offline.' },
  audioDownloadFailed: { ar: 'تعذّر تنزيل بعض الأصوات. جرّب مرة أخرى.', en: 'Some audio could not be downloaded. Try again.' },
} as const satisfies Record<string, Bilingual>;

export type UiKey = keyof typeof UI;

export const PRAISE_KEYS: UiKey[] = ['praiseWellDone', 'praiseExcellent', 'greatJob'];
